import { useLocation, useNavigate } from 'react-router-dom'

import { toFilter } from '../utils'
import useQueryStringParser, {
  getQueryStringParser,
} from './useQueryStringParser'

/**
 * Custom hook to manage URL query parameters and navigation.
 * Provides current query params, location, and a function to update the query string.
 */
const useRouter = () => {
  // Hook to programmatically navigate
  const navigate = useNavigate()
  // Hook to get current location object
  const location = useLocation()
  // Parse current query string into an object
  const urlParams = useQueryStringParser(window.location.search)

  /**
   * Updates the URL query parameters and navigates to the new URL.
   * @param newParams - Object with new/updated query parameters
   * @param replace - If true, replaces the current entry in the history stack
   * @param allowNullish - If true, allows nullish values in the query string
   */
  const updateQuery = (
    newParams: Record<string, any>,
    replace = false,
    allowNullish = false
  ) => {
    // Get current query params as an object
    const urlParams = getQueryStringParser()
    // Merge current params with new params
    const params = { ...urlParams, ...newParams }

    // Create a new URLSearchParams instance
    const searchParams = new URLSearchParams()

    // Iterate over all params to build the query string
    Object.keys(params).forEach((k) => {
      const value = params[k]

      if (Array.isArray(value)) {
        // If value is an array, convert it to a filter string
        searchParams.append(k, toFilter(value)!)
      } else {
        if (allowNullish) {
          // Allow nullish values if specified
          searchParams.append(k, value)
        } else if (
          value !== undefined &&
          value !== null &&
          value !== 'undefined' &&
          value !== 'null' &&
          value !== ''
        ) {
          // Only append non-nullish, non-empty values
          searchParams.append(k, value)
        } else {
          // Remove the key if value is nullish or empty
          searchParams.delete(k)
        }
      }
    })

    // Build the search string
    const search = '?' + searchParams.toString()

    // Navigate to the new URL with updated query string
    return navigate({ ...location, search }, { replace, state: location.state })
  }

  // Return current query params, location, and updateQuery function
  return { query: urlParams, location, updateQuery }
}

export default useRouter
